/** Base URL sem barra final (ex.: '' para mesmo origin, ou 'https://app.exemplo.com'). */
export function joinApiBase(base: string, path: string): string {
    const b = base.replace(/\/$/, '');
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${b}${p}`;
}

export type ApiBootstrap = {
    default_country_id: number | null;
    /** Tema visual padrão (Formulários → Widget), quando o embed não informa `themeId`. */
    default_form_theme_id: number | null;
    /** Título exibido no topo do widget (Formulários → Widget). */
    widget_title?: string | null;
    /** Texto ao lado do checkbox de aceite (Formulários → Widget). */
    acceptance_term_label?: string | null;
    /** URL pública do modelo DOCX do termo (placeholders), se enviado. */
    acceptance_term_docx_url?: string | null;
    /** Chave pública Pagar.me (checkout transparente). */
    pagarme_public_key?: string | null;
    /** Checkout simulado: fora de produção e sem Pagar.me configurada. */
    payment_simulation?: boolean;
    /** Mensagem após confirmação de pagamento (Configurações → Integrações → Pagar.me). */
    widget_payment_success_message?: string | null;
    /** Texto introdutório em erros de pagamento no widget. */
    widget_payment_error_intro?: string | null;
    /** Nome da instituição. */
    institution_name?: string | null;
    /** URL de contato da instituição. */
    contact_url?: string | null;
    /** HTML de contato exibido na tela de erro de pagamento (Formulários → Widget). */
    payment_error_contact_html?: string | null;
    /** HTML exibido quando o candidato já tem inscrição paga no DecolaEdu (Formulários → Widget). */
    already_enrolled_html?: string | null;
    /** HTML exibido quando o webhook retorna TEMMATRICULA=S (Formulários → Widget). */
    already_matriculated_html?: string | null;
};

export type ApiAdmissionType = { id: number; name: string };

export type ApiCombination = {
    payment_plan_id: number;
    admission_type_id: number;
    payment_method: string;
    installments: number;
    charge_all_installments?: boolean;
    amount: string;
    discount: string;
    liquid_amount: string;
    name: string;
    description: string | null;
    first_installment_due_days: number | null;
    recurring_installments_due_day: number | null;
};

export type ApiOfferResponse = {
    offer: {
        id: number;
        code: string;
        name: string;
        period: string | null;
        modality: string | null;
        active: boolean;
    };
    admission_types: ApiAdmissionType[];
    combinations: ApiCombination[];
};

export type ApiThemeResponse = {
    id: number;
    name: string;
    backgroundColor: string | null;
    textColor: string | null;
    accentColor: string | null;
    inputBackgroundColor: string | null;
    fonts: string | null;
    code: string | null;
};

export type ApiCepResponse = Record<string, unknown>;

export type ApiCpfInfo = {
    is_enrolled: boolean;
    has_matricula: boolean;
    is_alumni: boolean;
    alumni_admission_type_id: number | null;
};

const jsonHeaders: HeadersInit = { Accept: 'application/json' };

const GEOJS_URL = 'https://get.geojs.io/v1/ip/geo.json';

/** ViaCEP público — consultado no browser; o servidor só resolve IDs (país/estado/cidade). */
const viacepUrl = (cepDigits: string): string => `https://viacep.com.br/ws/${cepDigits}/json/`;

/** Falha antes da resposta HTTP (rede, CORS, offline). */
function isBrowserFetchNetworkFailure(e: unknown): boolean {
    if (e instanceof TypeError) {
        return true;
    }
    if (e instanceof DOMException && e.name === 'AbortError') {
        return true;
    }
    if (e instanceof Error) {
        const m = e.message.toLowerCase();
        if (m.includes('failed to fetch') || m.includes('networkerror') || m.includes('load failed') || m.includes('network request failed')) {
            return true;
        }
    }
    return false;
}

function mapFetchFailureToError(e: unknown, context: 'pagarme' | 'api' | 'viacep'): Error {
    if (!isBrowserFetchNetworkFailure(e)) {
        return e instanceof Error ? e : new Error(String(e));
    }
    if (context === 'pagarme') {
        return new Error(
            'Não foi possível conectar ao serviço de pagamento (Pagar.me). Verifique sua internet e tente novamente. Se o erro persistir, o domínio desta página pode precisar ser liberado no painel da Pagar.me.',
        );
    }
    if (context === 'viacep') {
        return new Error('Não foi possível consultar o CEP no serviço público. Verifique sua internet e tente novamente.');
    }
    return new Error('Não foi possível conectar ao servidor. Verifique sua internet e tente novamente.');
}

/** Campos alinhados ao backend (`StoreEnrollmentRequest` / inscrição). */
export type WidgetGeoFields = {
    geo_ip: string | null;
    latitude: number | null;
    longitude: number | null;
    geo_continent: string | null;
    geo_country: string | null;
    geo_region: string | null;
    geo_city: string | null;
};

/**
 * Geolocalização aproximada por IP (geojs.io). Falha silenciosa (retorna null).
 */
export async function fetchGeoJsGeo(): Promise<WidgetGeoFields | null> {
    try {
        const r = await fetch(GEOJS_URL, { headers: jsonHeaders });
        if (!r.ok) return null;
        const j = (await r.json()) as Record<string, unknown>;
        const lat = parseFloat(String(j.latitude ?? ''));
        const lng = parseFloat(String(j.longitude ?? ''));
        return {
            geo_ip: typeof j.ip === 'string' ? j.ip : null,
            latitude: Number.isFinite(lat) ? lat : null,
            longitude: Number.isFinite(lng) ? lng : null,
            geo_continent: typeof j.continent_code === 'string' ? j.continent_code : null,
            geo_country: typeof j.country === 'string' ? j.country : typeof j.country_code === 'string' ? j.country_code : null,
            geo_region: typeof j.region === 'string' ? j.region : null,
            geo_city: typeof j.city === 'string' ? j.city : null,
        };
    } catch {
        return null;
    }
}

const WIDGET_GEO_KEYS: (keyof WidgetGeoFields)[] = ['geo_ip', 'latitude', 'longitude', 'geo_continent', 'geo_country', 'geo_region', 'geo_city'];

/** Inclui no payload de progresso/inscrição os campos de geo já preenchidos no formData. */
export function appendWidgetGeoToPayload(payload: Record<string, unknown>, formData: Record<string, unknown>): void {
    for (const k of WIDGET_GEO_KEYS) {
        const v = formData[k];
        if (v === undefined || v === null || v === '') continue;
        payload[k] = v;
    }
}

export async function fetchBootstrap(apiBase: string): Promise<ApiBootstrap> {
    const r = await fetch(joinApiBase(apiBase, '/api/widget/bootstrap'), { headers: jsonHeaders });
    if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { message?: string };
        const msg =
            typeof j.message === 'string' && j.message.trim() !== ''
                ? j.message
                : 'Não foi possível carregar o formulário. Tente novamente em instantes.';
        throw new Error(msg);
    }
    return r.json() as Promise<ApiBootstrap>;
}

export async function fetchOfferLegacy(apiBase: string, offerId: string): Promise<ApiOfferResponse> {
    const q = new URLSearchParams({ offer_id: offerId });
    const r = await fetch(joinApiBase(apiBase, `/api/widget/offers/legacy?${q}`), { headers: jsonHeaders });
    if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { message?: string };
        const msg =
            typeof j.message === 'string' && j.message.trim() !== ''
                ? j.message
                : 'Não foi possível carregar o formulário de inscrição. Tente novamente em instantes.';
        throw new Error(msg);
    }
    return r.json() as Promise<ApiOfferResponse>;
}

export async function fetchCpfInfo(apiBase: string, email: string, cpf: string, offerId: number): Promise<ApiCpfInfo> {
    const params = new URLSearchParams({ email, cpf, offer_id: String(offerId) });
    const r = await fetch(joinApiBase(apiBase, `/api/widget/cpf-info?${params}`), { headers: jsonHeaders });
    if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { message?: string };
        const msg =
            typeof j.message === 'string' && j.message.trim() !== ''
                ? j.message
                : 'Não foi possível validar seus dados. Tente novamente em instantes.';
        throw new Error(msg);
    }
    return r.json() as Promise<ApiCpfInfo>;
}

export async function fetchFormTheme(apiBase: string, themeId: string): Promise<ApiThemeResponse | null> {
    if (!themeId || themeId === '0') return null;
    const r = await fetch(joinApiBase(apiBase, `/api/widget/form-themes/${encodeURIComponent(themeId)}`), {
        headers: jsonHeaders,
    });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error('Não foi possível carregar o tema do formulário. Tente novamente em instantes.');
    return r.json() as Promise<ApiThemeResponse>;
}

export async function fetchCep(apiBase: string, cepDigits: string): Promise<ApiCepResponse> {
    let viaRes: Response;
    try {
        viaRes = await fetch(viacepUrl(cepDigits), { headers: jsonHeaders });
    } catch (e) {
        throw mapFetchFailureToError(e, 'viacep');
    }
    let viaRaw: unknown;
    try {
        viaRaw = await viaRes.json();
    } catch {
        throw new Error('Não foi possível interpretar a resposta do ViaCEP. Verifique sua conexão e tente novamente.');
    }
    const via = (viaRaw ?? {}) as Record<string, unknown>;
    if (via.erro === true || via.erro === 'true') {
        throw new Error('Não encontramos este CEP. Confira os números ou preencha o endereço manualmente.');
    }
    if (!viaRes.ok) {
        throw new Error('Não foi possível consultar o CEP no serviço público. Tente novamente em instantes ou preencha o endereço manualmente.');
    }

    let r: Response;
    try {
        r = await fetch(joinApiBase(apiBase, '/api/widget/cep/resolve'), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ cep: cepDigits, via }),
        });
    } catch (e) {
        throw mapFetchFailureToError(e, 'api');
    }
    if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { message?: string };
        const msg = j.message;
        throw new Error(typeof msg === 'string' && msg.trim() !== '' ? msg : 'Não foi possível consultar o CEP agora. Tente novamente em instantes.');
    }
    return r.json() as Promise<ApiCepResponse>;
}

export async function fetchStates(apiBase: string, countryId: number): Promise<{ id: number; name: string; external_code: string | null }[]> {
    const r = await fetch(joinApiBase(apiBase, `/api/widget/countries/${countryId}/states`), {
        headers: jsonHeaders,
    });
    if (!r.ok) throw new Error('Não foi possível carregar os estados. Tente novamente em instantes.');
    const j = (await r.json()) as { states: { id: number; name: string; external_code: string | null }[] };
    return j.states;
}

export async function fetchCities(apiBase: string, stateId: number): Promise<{ id: number; name: string }[]> {
    const r = await fetch(joinApiBase(apiBase, `/api/widget/states/${stateId}/cities`), { headers: jsonHeaders });
    if (!r.ok) throw new Error('Não foi possível carregar as cidades. Tente novamente em instantes.');
    const j = (await r.json()) as { cities: { id: number; name: string }[] };
    return j.cities;
}

export type WidgetEnrollmentProgressStage = 'dados_basicos' | 'dados_endereco' | 'pagamento' | 'matricula';

export async function postEnrollmentProgress(
    apiBase: string,
    body: Record<string, unknown> & { session_id: string; offer_id: number; stage: WidgetEnrollmentProgressStage },
): Promise<{ id: number; widget_stage?: string }> {
    const r = await fetch(joinApiBase(apiBase, '/api/widget/enrollments/progress'), {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
    });
    const j = (await r.json().catch(() => ({}))) as {
        message?: string;
        errors?: Record<string, string[]>;
        id?: number;
        widget_stage?: string;
    };
    if (!r.ok) {
        if (j.errors && typeof j.errors === 'object') {
            const first = Object.values(j.errors).flat()[0];
            if (first) throw new Error(first);
        }
        const msg =
            typeof j.message === 'string' && j.message.trim() !== ''
                ? j.message
                : 'Não foi possível salvar seus dados agora. Tente novamente em instantes.';
        throw new Error(msg);
    }
    return j as { id: number; widget_stage?: string };
}

export type WidgetCouponValidateResponse = {
    coupon_id: number;
    code: string;
    scholarship_name: string;
    discount_type: 'P' | 'V' | string;
    amount: string;
};

export async function validateWidgetCoupon(apiBase: string, body: { offer_id: number; code: string }): Promise<WidgetCouponValidateResponse> {
    const r = await fetch(joinApiBase(apiBase, '/api/widget/coupons/validate'), {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
    });
    const j = (await r.json().catch(() => ({}))) as WidgetCouponValidateResponse & { message?: string };
    if (!r.ok) {
        const msg = typeof j.message === 'string' && j.message.trim() !== '' ? j.message : 'Não foi possível validar o cupom. Tente novamente.';
        throw new Error(msg);
    }
    return j as WidgetCouponValidateResponse;
}

const PAGARME_TOKENS_URL = 'https://api.pagar.me/core/v5/tokens';

export type WidgetPaymentCreateBody = {
    session_id: string;
    payment_method: 'pix' | 'cartao' | 'boleto';
    card_token?: string | null;
};

export type WidgetPaymentCreateResponse = {
    enrollment_id: number;
    order_id?: string | null;
    charge_id?: string | null;
    charge_status?: string | null;
    payment_method: string;
    pix?: { qr_code?: string | null; qr_code_url?: string | null; expires_at?: string | null };
    boleto?: { line?: string | null; pdf_url?: string | null; due_at?: string | null };
    card?: { status?: string | null; refused_reason?: string | null };
};

/** Resposta HTTP 200 com corpo inválido ou incompleto (não é necessariamente falha de rede, mas o utilizador deve tentar de novo). */
function mapInvalidPaymentApiResponse(method: 'pix' | 'boleto' | 'cartao'): Error {
    const byMethod: Record<'pix' | 'boleto' | 'cartao', string> = {
        pix: 'Não foi possível validar os dados do PIX. A resposta do servidor está incompleta ou houve falha de conexão. Tente novamente.',
        boleto: 'Não foi possível validar os dados do boleto. A resposta do servidor está incompleta ou houve falha de conexão. Tente novamente.',
        cartao: 'Não foi possível validar a resposta do pagamento com cartão. A resposta do servidor está incompleta ou houve falha de conexão. Tente novamente.',
    };
    return new Error(byMethod[method]);
}

function parseResponseJson(raw: string): unknown {
    const t = raw.trim();
    if (t === '') {
        return null;
    }
    return JSON.parse(t) as unknown;
}

function enrollmentIdMatchesPayload(payload: unknown, expected: number): boolean {
    if (payload === null || typeof payload !== 'object') {
        return false;
    }
    const id = (payload as Record<string, unknown>).enrollment_id;
    if (typeof id === 'number' && Number.isFinite(id)) {
        return id === expected;
    }
    if (typeof id === 'string' && /^\d+$/.test(id)) {
        return parseInt(id, 10) === expected;
    }
    return false;
}

/**
 * Garante que a resposta de criação de cobrança PIX/boleto/cartão contém o necessário para o widget.
 */
function assertValidWidgetPaymentCreateResponse(
    payload: unknown,
    expectedEnrollmentId: number,
    method: 'pix' | 'boleto' | 'cartao',
): asserts payload is WidgetPaymentCreateResponse {
    if (payload === null || typeof payload !== 'object') {
        throw mapInvalidPaymentApiResponse(method);
    }
    const o = payload as Record<string, unknown>;

    if (!enrollmentIdMatchesPayload(payload, expectedEnrollmentId)) {
        throw mapInvalidPaymentApiResponse(method);
    }

    const pm = o.payment_method;
    if (pm !== method) {
        throw mapInvalidPaymentApiResponse(method);
    }

    const orderId = o.order_id;
    const chargeId = o.charge_id;
    const hasOrderRef =
        (typeof orderId === 'string' && orderId.trim() !== '') ||
        (typeof orderId === 'number' && Number.isFinite(orderId)) ||
        (typeof chargeId === 'string' && chargeId.trim() !== '') ||
        (typeof chargeId === 'number' && Number.isFinite(chargeId));
    if (!hasOrderRef) {
        throw mapInvalidPaymentApiResponse(method);
    }

    const st = String(o.charge_status ?? '').toLowerCase();
    if (st === 'failed' || st === 'canceled' || st === 'cancelled') {
        const msg = typeof o.message === 'string' && o.message.trim() !== '' ? o.message.trim() : null;
        throw new Error(msg ?? 'Não foi possível gerar o pagamento. Tente outra forma ou mais tarde.');
    }

    if (method === 'pix') {
        const pix = o.pix;
        if (pix === null || typeof pix !== 'object') {
            throw mapInvalidPaymentApiResponse('pix');
        }
        const px = pix as Record<string, unknown>;
        const qr = String(px.qr_code ?? '').trim();
        const qrUrl = String(px.qr_code_url ?? '').trim();
        const qrOk = qr.length >= 20;
        const urlOk = qrUrl.startsWith('http://') || qrUrl.startsWith('https://');
        if (!qrOk && !urlOk) {
            throw mapInvalidPaymentApiResponse('pix');
        }
        return;
    }

    if (method === 'boleto') {
        const bol = o.boleto;
        if (bol === null || typeof bol !== 'object') {
            throw mapInvalidPaymentApiResponse('boleto');
        }
        const b = bol as Record<string, unknown>;
        const lineDigits = String(b.line ?? '').replace(/\D/g, '');
        const pdf = String(b.pdf_url ?? '').trim();
        const lineOk = lineDigits.length >= 40;
        const pdfOk = pdf.startsWith('http://') || pdf.startsWith('https://');
        if (!lineOk && !pdfOk) {
            throw mapInvalidPaymentApiResponse('boleto');
        }
        return;
    }

    const card = o.card;
    if (card === null || typeof card !== 'object') {
        throw mapInvalidPaymentApiResponse('cartao');
    }
}

function assertValidWidgetPaymentStatusPayload(payload: unknown): asserts payload is {
    paid: boolean;
    charge_status?: string | null;
    order_status?: string | null;
} {
    if (payload === null || typeof payload !== 'object') {
        throw new Error(
            'Não foi possível validar o status do pagamento. A resposta do servidor está incompleta ou houve falha de conexão. Tente novamente.',
        );
    }
    const o = payload as Record<string, unknown>;
    if (typeof o.paid !== 'boolean') {
        throw new Error(
            'Não foi possível validar o status do pagamento. A resposta do servidor está incompleta ou houve falha de conexão. Tente novamente.',
        );
    }
}

export async function postWidgetPayment(apiBase: string, enrollmentId: number, body: WidgetPaymentCreateBody): Promise<WidgetPaymentCreateResponse> {
    let r: Response;
    try {
        r = await fetch(joinApiBase(apiBase, `/api/widget/enrollments/${enrollmentId}/payment`), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
        });
    } catch (e) {
        throw mapFetchFailureToError(e, 'api');
    }

    let raw: string;
    try {
        raw = await r.text();
    } catch {
        throw mapFetchFailureToError(new TypeError('failed to read body'), 'api');
    }

    let parsed: unknown;
    try {
        parsed = parseResponseJson(raw);
    } catch {
        throw new Error('Não foi possível interpretar a resposta do servidor de pagamento. Verifique sua conexão e tente novamente.');
    }

    const errPayload = (parsed ?? {}) as { message?: string };

    if (!r.ok) {
        const msg =
            typeof errPayload.message === 'string' && errPayload.message.trim() !== ''
                ? errPayload.message
                : 'Não foi possível iniciar o pagamento. Tente novamente.';
        throw new Error(msg);
    }

    if (parsed === null) {
        throw mapInvalidPaymentApiResponse(body.payment_method);
    }

    try {
        assertValidWidgetPaymentCreateResponse(parsed, enrollmentId, body.payment_method);
    } catch (e) {
        if (e instanceof Error) {
            throw e;
        }
        throw mapInvalidPaymentApiResponse(body.payment_method);
    }

    return parsed as WidgetPaymentCreateResponse;
}

export async function getWidgetPaymentStatus(
    apiBase: string,
    enrollmentId: number,
    sessionId: string,
): Promise<{ paid: boolean; charge_status?: string | null; order_status?: string | null }> {
    const q = new URLSearchParams({ session_id: sessionId });
    let r: Response;
    try {
        r = await fetch(joinApiBase(apiBase, `/api/widget/enrollments/${enrollmentId}/payment/status?${q}`), {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
    } catch (e) {
        throw mapFetchFailureToError(e, 'api');
    }

    let raw: string;
    try {
        raw = await r.text();
    } catch {
        throw mapFetchFailureToError(new TypeError('failed to read body'), 'api');
    }

    let parsed: unknown;
    try {
        parsed = parseResponseJson(raw);
    } catch {
        throw new Error('Não foi possível interpretar a resposta ao consultar o pagamento. Verifique sua conexão e tente novamente.');
    }

    const j = (parsed ?? {}) as {
        paid?: boolean;
        charge_status?: string | null;
        order_status?: string | null;
        message?: string;
    };

    if (!r.ok) {
        const msg = typeof j.message === 'string' && j.message.trim() !== '' ? j.message : 'Não foi possível consultar o pagamento.';
        throw new Error(msg);
    }

    if (parsed === null) {
        throw new Error(
            'Não foi possível validar o status do pagamento. A resposta do servidor está incompleta ou houve falha de conexão. Tente novamente.',
        );
    }

    assertValidWidgetPaymentStatusPayload(parsed);

    const o = parsed as Record<string, unknown>;
    return {
        paid: o.paid as boolean,
        charge_status: typeof o.charge_status === 'string' || o.charge_status === null ? o.charge_status : null,
        order_status: typeof o.order_status === 'string' || o.order_status === null ? o.order_status : null,
    };
}

export type PagarmeCardFields = {
    number: string;
    holder_name: string;
    exp_month: number;
    exp_year: number;
    cvv: string;
};

/**
 * Tokenização no browser (chave pública) via POST em `api.pagar.me/core/v5/tokens`.
 * A autenticação deste endpoint é feita **exclusivamente** pela chave pública no query
 * param `appId` — não envie header `Authorization` nem a chave secreta. Apenas
 * `Content-Type` é permitido (https://docs.pagar.me/reference/criar-token-cart%C3%A3o-1).
 * O navegador aplica CORS: a Pagar.me só libera a origem se o domínio estiver cadastrado
 * no painel (liberação de domínio). Isso vale para o site do DecolaEdu e para cada
 * domínio onde o widget for embutido. Fluxo oficial alternativo: tokenizecard.js
 * (https://docs.pagar.me/reference/pagarme-js).
 */
export async function createPagarmeCardToken(publicKey: string, card: PagarmeCardFields): Promise<string> {
    const url = `${PAGARME_TOKENS_URL}?appId=${encodeURIComponent(publicKey)}`;
    let r: Response;
    try {
        r = await fetch(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                type: 'card',
                card: {
                    number: String(card.number).replace(/\D/g, ''),
                    holder_name: card.holder_name.trim(),
                    exp_month: card.exp_month,
                    exp_year: card.exp_year,
                    cvv: String(card.cvv).replace(/\D/g, ''),
                },
            }),
        });
    } catch (e) {
        throw mapFetchFailureToError(e, 'pagarme');
    }
    const j = (await r.json().catch(() => ({}))) as { id?: string; message?: string; errors?: unknown };
    if (!r.ok) {
        const msg = typeof j.message === 'string' && j.message.trim() !== '' ? j.message : 'Cartão recusado ou dados inválidos.';
        throw new Error(msg);
    }
    if (!j.id) {
        throw new Error('Não foi possível tokenizar o cartão.');
    }
    return j.id;
}

/**
 * Sinaliza ao backend que o aluno entrou na tela de digitar o cartão, para que o
 * evento de pagamento pendente seja enviado ao Rubeus antes da cobrança.
 * Best-effort: não bloqueia o fluxo se falhar.
 */
export async function postEnrollmentPaymentPending(apiBase: string, enrollmentId: number, sessionId: string): Promise<void> {
    try {
        await fetch(joinApiBase(apiBase, `/api/widget/enrollments/${enrollmentId}/payment-pending`), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ session_id: sessionId }),
        });
    } catch {
        /* best-effort */
    }
}

export async function postEnrollment(apiBase: string, body: Record<string, unknown>): Promise<{ id: number; message: string }> {
    const r = await fetch(joinApiBase(apiBase, '/api/widget/enrollments'), {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
    });
    const j = (await r.json().catch(() => ({}))) as {
        message?: string;
        errors?: Record<string, string[]>;
        id?: number;
    };
    if (!r.ok) {
        if (j.errors && typeof j.errors === 'object') {
            const first = Object.values(j.errors).flat()[0];
            if (first) throw new Error(first);
        }
        const msg =
            typeof j.message === 'string' && j.message.trim() !== ''
                ? j.message
                : 'Não foi possível concluir a inscrição agora. Tente novamente em instantes.';
        throw new Error(msg);
    }
    return j as { id: number; message: string };
}

/** Checkout simulado (homologação sem Pagar.me): marca pago no backend e dispara a baixa. */
export async function simulateWidgetPayment(apiBase: string, enrollmentId: number, sessionId: string): Promise<void> {
    const res = await fetch(joinApiBase(apiBase, `/api/widget/enrollments/${enrollmentId}/payment/simulate`), {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ session_id: sessionId }),
    });
    if (!res.ok) {
        throw new Error('Não foi possível concluir o pagamento simulado.');
    }
}

/** Dispara integração da ficha em fila; não aguarda resposta (checkout segue em paralelo). */
export function enqueueWidgetEnrollmentIntegration(apiBase: string, enrollmentId: number, sessionId: string): void {
    void fetch(joinApiBase(apiBase, `/api/widget/enrollments/${enrollmentId}/integrate`), {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ session_id: sessionId }),
    }).catch(() => {
        /* background */
    });
}

export async function postWidgetEvent(
    apiBase: string,
    payload: { event: string; session_id?: string; offer_id?: string; theme_id?: string; payload?: Record<string, unknown> },
): Promise<void> {
    try {
        await fetch(joinApiBase(apiBase, '/api/widget/events'), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });
    } catch {
        /* fire-and-forget */
    }
}
